import { Observable, Subject } from 'rxjs'

export type TableDataRowState<TableRow> = TableRow & { $selected: boolean }

export interface TableList<TableRow> {
  total: number
  enable_total?: number
  list: TableRow[]
  terminal_count?: number
}

export interface TableState<TableRow> {
  selectAll: boolean,
  selectCount: number,
  pageSelectCount: number,
  data: TableList<TableRow>
}

export interface TableRequestParams {
  page: number
  limit: number
  useMemory: boolean
}

/**
 * 表格内存分页控制类
 *
 * @description
 * 用于表格跨页选择、分布查看已选择的数据等功能，在实际使用时需继承 TableMemoryPagination 类，并实现其抽象方法。
 * 和其它正常数据请求不一样，使用 TableMemoryPagination 获取数据时，不能直接调用其 getListByServer 方法，而应通过 refresh 拉数，
 * 再用 onStateChange 订阅或 getViewState 主动拉取快照，将状态同步到视图。发送请求应通过 refresh 触发。
 */
export abstract class TableMemoryPagination<RequestParams extends TableRequestParams, TableRow> {
  private state: TableState<TableDataRowState<TableRow>> = {
    selectAll: false, // 是否全选
    selectCount: 0, // 全部选择计数
    pageSelectCount: 0, // 当页选择计数
    data: {
      total: 0,
      terminal_count: 0,
      enable_total: 0,
      list: []
    }
  }

  private cache = {
    memoryData: [] as TableDataRowState<TableRow>[], // 展示选中数据的缓存
    lastQueryParams: {} as RequestParams // 上一次请求缓存 用于判断是否需要拷贝一份selectData
  }

  private selectedData: TableDataRowState<TableRow>[] = [] // 选中数据
  private noReflush = true // 是否刷新页面
  private stateChangeEvent = new Subject<TableState<TableDataRowState<TableRow>>>()

  /**
   * 用于format 服务器请求key
   */
  private keyTotal = 'total'
  private keyEnableTotal = 'enable_total'
  private keyList = 'list'
  isDisabled: (row) => boolean = () => false

  /**
   * 表格状态数据观察者对象。你应该始终从这里获取数据。
   */
  onStateChange: Observable<TableState<TableDataRowState<TableRow>>>

  protected constructor () {
    // eslint-disable-next-line no-magic-numbers
    this.onStateChange = this.stateChangeEvent.asObservable()
  }

  /**
   * 子类需要实现的从服务器获取数据的方法，TableMemoryPagination 会根据查询条件来调用此方法。你不应该主动调用此方法
   * @param params
   */
  abstract getListByServer (params: RequestParams): Promise<TableList<TableRow>>

  /**
    * 子类需要实现的数据对比方法，TableMemoryPagination 会在做去重操作的时候调用
    * @param left
    * @param right
    */
  abstract isEqual (left: TableRow, right: TableRow): boolean

  /**
   * 获取已选择的数据
   */
  getSelectedData () {
    return this.selectedData.slice()
  }

  /**
   * 当前表格状态快照（与 onStateChange 推送结构一致）。
   * 供不订阅 Observable 的场景在 refresh 或选择操作后主动同步视图。
   */
  getViewState (): TableState<TableDataRowState<TableRow>> {
    return {
      ...this.state,
      selectCount: this.state.selectAll
        ? (this.state.data.enable_total || this.state.data.total)
        : this.selectedData.length,
    }
  }

  /**
   * 将当前展示的列表同步到内部 state.data（不请求接口）。
   * 用于自行拉取列表、未走 refresh 的页面，否则 mergeSelectedData 无法在 state.data.list 中定位行。
   */
  syncVisibleList (list: TableDataRowState<TableRow>[], total: number, enableTotal?: number) {
    this.state.data = {
      ...this.state.data,
      total,
      enable_total: enableTotal ?? this.state.data.enable_total,
      list
    }
    this.state.pageSelectCount = 0
    list.forEach(el => {
      el.$selected = (this.state.selectAll && !this.isDisabled(el))
        ? true
        : !!(this.selectedData.find(row => this.isEqual(row, el)))
      if (el.$selected) {
        this.state.pageSelectCount++
      }
    })
    this.stateChangeEvent.next({
      ...this.state,
      selectCount: this.state.selectAll ? (this.state.data.enable_total || this.state.data.total) : this.selectedData.length
    })
  }

  /**
   * 刷新数据，TableMemoryPagination 会根椐查询条件，把最新的数据通过 onStateChange 观察者通知订阅者
   * @param params 请求参数
   * @param isLoadMore 是否是加载更多（滚动分页模式），如果是 true 且 page > 1，则追加数据而不是替换
   */
  async refresh (params: RequestParams, isLoadMore: boolean = false) {
    await this.fetchData(params).then(response => {
      this.noReflush = true

      if (isLoadMore && params.page > 1) {
        this.state.data.list = [...this.state.data.list, ...response.list]
        this.state.data.total = response.total
      } else {
        this.state.data = response
      }
      // 追加/替换后统一先清零再对完整列表重算，避免追加模式下翻倍累加
      this.state.pageSelectCount = 0
      this.state.data.list.forEach(el => {
        el.$selected = (this.state.selectAll && !this.isDisabled(el)) ? true : !!(this.selectedData.find(row => this.isEqual(row, el)))
        if (el.$selected) {
          this.state.pageSelectCount++
        }
      })
      this.stateChangeEvent.next({
        ...this.state,
        selectCount: this.state.selectAll ? (this.state.data.enable_total || this.state.data.total) : this.selectedData.length,
      })
    })
  }

  /**
   * 合并选择数据，如果有第二个参数，则视为在当前已选择的数据中排除。（主要是适配 ElementUI 的逻辑）
   * @param items 最新选择的数据（内部会做去重操作）
   * @param currentRow 要排队的数据
   */
  mergeSelectedData (items: TableDataRowState<TableRow>[], currentRow?: TableDataRowState<TableRow>) {
    if (this.state.selectAll) return
    if (this.rowsMergeSelectedData(items, currentRow)) {
      this.stateChangeEvent.next({
        ...this.state,
        selectCount: this.selectedData.length,
      })
    }
  }

  /**
   * 设置全选所有数据，这里的所有数据指服务端的所有数据，而不是指前段当前页
   */
  selectAll () {
    this.selectedData = []
    this.state.selectAll = true
    this.noReflush = false
    this.stateChangeEvent.next({
      ...this.state,
      selectCount: this.state.data.list.length,
      data: {
        ...this.state.data,
        list: this.state.data.list.map(el => (el.$selected = true) && el)
      }
    })
  }

  /**
   * 取消选择指定行
   * @param currentRow 要取消的行
   */
  cancelSelect (currentRow?: TableDataRowState<TableRow>) {
    if (currentRow.$selected) {
      this.mergeSelectedData(this.selectedData, currentRow)
    }
  }

  /**
   * 设置取消全选所有数据，这里的所有数据指服务端的所有数据，而不是指前段当前页
   */
  cancelSelectAll () {
    if (this.state.selectAll) {
      this.state.selectAll = false
      this.noReflush = false
      this.state.pageSelectCount = 0
      this.state.data.list.forEach(el => { el.$selected = false })
      this.stateChangeEvent.next({
        ...this.state,
        selectCount: 0,
        data: {
          ...this.state.data,
          list: this.state.data.list,
        }
      })
      return
    }

    this.selectedData = []
    this.noReflush = false
    this.stateChangeEvent.next({
      ...this.state,
      selectCount: 0,
      data: {
        ...this.state.data,
        list: this.state.data.list.map(el => (el.$selected = false) || el),
      }
    })
  }

  /**
   * 切换当前页全选状态
   */
  togglePageSelect (check?: boolean) {
    this.noReflush = false
    const list = this.state.data.list.filter(v => !this.isDisabled(v))
    if (
      (typeof check !== 'undefined' && check) ||
      (this.state.pageSelectCount !== list.length)
    ) {
      this.mergeSelectedData(list)
    } else {
      this.mergeSelectedData([])
    }
  }

  // 选中行合并到selectData
  private rowsMergeSelectedData (items: TableDataRowState<TableRow>[], currentRow?: TableDataRowState<TableRow>) {
    let done = false
    if (currentRow) { // 手动 - 单选
      done = true
      const idx = this.selectedData.findIndex(el => this.isEqual(el, currentRow))
      const ele = this.state.data.list.find(el => this.isEqual(el, currentRow))
      const rowForPush = ele ?? currentRow
      const syncRefs = ele && ele !== currentRow ? [ele, currentRow] : [rowForPush]
      if (idx !== -1) {
        syncRefs.forEach(r => { r.$selected = false })
        this.selectedData.splice(idx, 1)
        this.state.pageSelectCount--
      } else {
        syncRefs.forEach(r => { r.$selected = true })
        this.selectedData.push(rowForPush)
        this.state.pageSelectCount++
      }
    } else { // 自动 - 只做当页全选 / 反选
      const list = this.state.data.list.filter(v => !this.isDisabled(v))
      if (items.length === list.length) {
        done = true
        this.state.pageSelectCount = list.length
        list.forEach(row => {
          row.$selected = true
          const idx = this.selectedData.findIndex(el => this.isEqual(el, row))
          if (idx === -1) {
            this.selectedData.push(row)
          }
        })
      } else if (items.length === 0 && !this.noReflush) {
        done = true
        this.state.pageSelectCount = 0
        this.state.data.list.forEach(row => {
          row.$selected = false
          const idx = this.selectedData.findIndex(el => this.isEqual(el, row))
          if (idx !== -1) {
            this.selectedData.splice(idx, 1)
          }
        })
      }
    }
    this.noReflush = false
    return done
  }

  // 发请求
  private fetchData (params: RequestParams): Promise<TableList<TableDataRowState<TableRow>>> {
    if (this.cache.lastQueryParams.useMemory !== params.useMemory) { // change
      if (params.useMemory && !this.state.selectAll) {
        this.cache.memoryData = this.selectedData.slice()
      } else {
        this.cache.memoryData = []
      }
      this.cache.lastQueryParams = { ...params }
    }
    if (params.useMemory && !this.state.selectAll) {
      return this.localData(params)
    }
    return this.getListByServer(params).then(response => ({
      total: response[this.keyTotal],
      enable_total: response[this.keyEnableTotal],
      list: response[this.keyList]
    }))
  }

  // 本地内存数据
  private localData (params: RequestParams): Promise<TableList<TableDataRowState<TableRow>>> {
    const startIndex = (params.page - 1) * params.limit
    return Promise.resolve({
      total: this.cache.memoryData.length,
      enable_total: this.state.data.enable_total,
      list: this.cache.memoryData.slice(startIndex, startIndex + params.limit)
    })
  }
}
